import ProductPricingFields from '../components/ProductPricingFields';
import { resolveProductPricing, calculateMarginPrice } from '../utils/productPricing.mjs';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Modal, Toast, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import FormContainer from '../components/FormContainer';
import {
  fetchProductDetails,
  updateProduct,
  uploadProductImage,
  resetUpdateProduct,
  resetImageUpload,
  deleteImages,
  importProductReviews,
  resetImportReviews,
} from '../slices/productSlice';

import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import axios from 'axios';
import CategoryPicker from '../components/CategoryPicker';
import Rating from '../components/Rating';
import { parseAliHtml } from '../utils/aliHtmlParser';
import { parseAliReviewHtml } from '../utils/aliReviewHtmlParser';

import VariantEditor from '../components/VariantEditor.jsx'

const ProductEditScreen = ({ match, history }) => {
  const productId = match.params.id;
  const dispatch = useDispatch();
  const userInfo = useSelector((s) => s.user?.userInfo);
  const uploadAuthConfig = { withCredentials: true, headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const {
    loading: loadingDetails,
    error: errorDetails,
    productDetails,
    loadingUpdate,
    errorUpdate,
    successUpdate,
    successImageUpload,
    loadingImageUpload,
    errorImageUpload,
    product, // pull latest product from slice for post-upload sync
    loadingImportReviews,
    successImportReviews,
    errorImportReviews,
  } = useSelector((state) => ({
    ...state.productList,
    product: state.productList.product,
  }));

  // Access category entities to derive name/path from selectedCategoryId
  const { byId: categoryById } = useSelector((state) => state.categories || {});

  const [name, setName] = useState('');
  const [purchaseLink, setPurchaseLink] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const [returnsPolicy, setReturnsPolicy] = useState('');
  const [price, setPrice] = useState(0);
  const [pricing, setPricing] = useState({ pricingMethod: 'manual', costPrice: '', profitMargin: '' });
  const [image, setImage] = useState('');
  const [imageFolderName, setImageFolderName] = useState('Undefined');
  const [imageC, setImageC] = useState([]);
  const [imageB, setImageB] = useState([]);
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  // Taxonomy selection
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedIsLeaf, setSelectedIsLeaf] = useState(false);
  const [prefillCategoryPath, setPrefillCategoryPath] = useState([]);
  const [countInStock, setCountInStock] = useState(0);
  const [video, setVideo] = useState('');
  const [colorsVariants, setColorsVariants] = useState([]);
  // New variant system state
  const [variantProps, setVariantProps] = useState([]);
  const [skus, setSkus] = useState([]);
  const [description, setDescription] = useState(EditorState.createEmpty());
  const [disabled, setDisabled] = useState(false);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  // New state for server-side gallery browser
  const [showGallery, setShowGallery] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  // Track what we're selecting for: main image or a specific color variant index
  const [galleryTarget, setGalleryTarget] = useState({ type: 'main', index: null });

  // Client-side upload states
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadValidationError, setUploadValidationError] = useState('');
  const [localUploadError, setLocalUploadError] = useState('');
  const uploadAbortRef = useRef(null);
  // Unified error modal for this screen (replaces inline red messages)
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });
  const lastShownErrorKeyRef = useRef('');
  // Add local submitting state to guard double-clicks
  const [submitting, setSubmitting] = useState(false);
  // Show a toast on successful save
  const [showSavedToast, setShowSavedToast] = useState(false);
  // Track mount status to avoid state updates after unmount during Save-and-Exit
  const mountedRef = useRef(false);

  // Hydration guards to prevent stale data and duplicate initialization
  const fetchedForIdRef = useRef(null);
  const initializedForIdRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);
  // Deletion UI state (frontend-only)
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState(() => new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteContext, setDeleteContext] = useState({ type: 'single', srcs: [] });
  const [deleteError, setDeleteError] = useState('');
  const longPressRef = useRef({ timer: null, src: '' });
  // Track the most recently saved product to resolve canonical path after save
  const lastSavedProductRef = useRef(null);

  // Unsaved-changes guard state
  const [isDirty, setIsDirty] = useState(false);
  const initialSnapshotRef = useRef(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [exitAfterSave, setExitAfterSave] = useState(false);
  const [, setExitTarget] = useState(null);
  const [pendingNavAction, setPendingNavAction] = useState('PUSH');
  // Track what the user intended when modal opened: view product page or go back to list
  const [exitIntent, setExitIntent] = useState('other'); // 'viewProduct' | 'backToList' | 'other'
  const unblockRef = useRef(null);

  // External URL support state
  const [useExternalMain, setUseExternalMain] = useState(false);
  const [externalMainUrl, setExternalMainUrl] = useState('');
  const [externalMainError, setExternalMainError] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [galleryUrlError, setGalleryUrlError] = useState('');
  // HTML paste-parse state
  const [rawHtml, setRawHtml] = useState('');
  const [priceSourceBadge, setPriceSourceBadge] = useState('');
  const [parseWarnings, setParseWarnings] = useState([]);
  const [parsing, setParsing] = useState(false);

  // Review import (AliExpress review HTML)
  const [rawReviewHtml, setRawReviewHtml] = useState('');
  const [reviewParseWarnings, setReviewParseWarnings] = useState([]);
  const [parsedReviews, setParsedReviews] = useState([]);
  const [parsingReviews, setParsingReviews] = useState(false);
  const [importLimit, setImportLimit] = useState('');
  const [selectedReviewIdx, setSelectedReviewIdx] = useState(() => new Set());

  const importLimitNum = Number(importLimit);
  const effectiveReviews = (importLimitNum > 0)
    ? (Array.isArray(parsedReviews) ? parsedReviews.slice(0, importLimitNum) : [])
    : (Array.isArray(parsedReviews) ? parsedReviews : []);

  const isValidHttpUrl = useCallback((value) => {
    if (!value || typeof value !== 'string') return false;
    try {
      const u = new URL(value.trim());
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }, []);

  const getErrorMessage = useCallback((err, fallback = 'Request failed.') => {
    // Handles:
    // - axios errors: err.response.data.message
    // - RTK unwrap rejections: string, {message}, {payload}, {payload:{message}}
    // - plain Error
    if (!err) return fallback;
    if (typeof err === 'string') return err;

    const e = err;
    const fromAxios = e?.response?.data?.message;
    if (typeof fromAxios === 'string' && fromAxios.trim()) return fromAxios.trim();
    const fromPayloadMessage = e?.payload?.message;
    if (typeof fromPayloadMessage === 'string' && fromPayloadMessage.trim()) return fromPayloadMessage.trim();
    const fromPayload = e?.payload;
    if (typeof fromPayload === 'string' && fromPayload.trim()) return fromPayload.trim();
    const fromMessage = e?.message;
    if (typeof fromMessage === 'string' && fromMessage.trim()) return fromMessage.trim();
    const fromDataMessage = e?.data?.message;
    if (typeof fromDataMessage === 'string' && fromDataMessage.trim()) return fromDataMessage.trim();

    try {
      const asJson = JSON.stringify(e);
      if (asJson && asJson !== '{}' && asJson !== 'null') return asJson;
    } catch (_) {}
    return fallback;
  }, []);

  const openErrorModal = useCallback((title, message) => {
    const t = String(title || 'Error');
    const m = String(message || '').trim();
    const key = `${t}::${m}`;
    if (!m || lastShownErrorKeyRef.current === key) return;
    lastShownErrorKeyRef.current = key;
    setErrorModal({ show: true, title: t, message: m });
  }, []);

  const closeErrorModal = useCallback(() => {
    setErrorModal((s) => ({ ...s, show: false }));
    // Allow the same exact message to be shown again on a future attempt
    lastShownErrorKeyRef.current = '';
  }, []);

  // Ensure a src is present in the gallery list (imageB) locally
  const ensureInGallery = useCallback((src) => {
    const s = (src || '').trim();
    if (!s) return;
    setImageB((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const exists = list.some((it) => (it?.src || '').trim() === s);
      return exists ? list : [...list, { src: s }];
    });
  }, []);

  // Normalize any src to a web path under /uploads and unify slashes
  const normalizeToWebPath = useCallback((src, folder) => {
    if (!src || typeof src !== 'string') return src;
    // If already an http(s) URL, keep as-is
    try {
      const u = new URL(src.trim());
      if (u.protocol === 'http:' || u.protocol === 'https:') return src.trim();
    } catch (_) {}
    // unify backslashes
    let s = src.replace(/\\/g, '/');
    // if an absolute windows path leaked like /C:/... or C:/...
    if (/^\/?[A-Za-z]:\//.test(s)) {
      const base = s.split('/').pop();
      if (folder && base) return `/uploads/${folder}/${base}`;
    }
    return s;
  }, []);

  // Build a normalized snapshot of current form state for dirty detection
  const buildSnapshot = useCallback(() => {
    // derive description HTML
    let descriptionHtml = '';
    try {
      const raw = convertToRaw(description.getCurrentContent());
      descriptionHtml = draftToHtml(raw);
    } catch (_) {}

    // normalize gallery and variants arrays
    const imageBList = Array.isArray(imageB)
      ? imageB.map((it) => (it?.src || '').trim()).filter(Boolean)
      : [];
    const variants = Array.isArray(colorsVariants)
      ? colorsVariants.map((v) => ({
          name: (v?.name || '').trim(),
          src: (v?.src || '').trim(),
          price:
            typeof v?.price === 'number' && !Number.isNaN(v.price) && v.price >= 0
              ? Number(v.price)
              : '',
          costPrice: v.costPrice,
          useBasePrice: v.useBasePrice,
          unavailable: !!v?.unavailable,
        }))
      : [];

    return {
      name: (name || '').trim(),
      disabled: !!disabled,
      purchaseLink: (purchaseLink || '').trim(),
      shippingMethod: (shippingMethod || '').trim(),
      returnsPolicy: (returnsPolicy || '').trim(),
      price: Number(price) || 0,
      ...pricing,
      image: (image || '').trim(),
      imageFolderName: (imageFolderName || '').trim(),
      imageB: imageBList,
      colorsVariants: variants,
      variantProps: Array.isArray(variantProps) ? variantProps : [],
      skus: Array.isArray(skus) ? skus : [],
      brand: (brand || '').trim(),
      category: (category || '').trim(),
      selectedCategoryId: selectedCategoryId || null,
      categoryPath: Array.isArray(prefillCategoryPath) ? prefillCategoryPath : [],
      countInStock: Number(countInStock) || 0,
      video: (video || '').trim(),
      descriptionHtml: (descriptionHtml || '').trim(),
      hasPendingUploads: Array.isArray(imageC) && imageC.length > 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing omission of 'disabled', not part of this cleanup pass
  }, [pricing, name, purchaseLink, shippingMethod, returnsPolicy, price, image, imageFolderName, imageB, colorsVariants, variantProps, skus, brand, category, selectedCategoryId, prefillCategoryPath, countInStock, video, description, imageC]);

  const isEqualSnapshot = useCallback((a, b) => {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }, []);

  // Hydrate all local form fields from a product once per product _id
  const hydrateFromProduct = useCallback((p) => {
    if (!p) return;
    const folder = p.imageFolderName || imageFolderName || 'Undefined';

    setName(p.name || '');
    setDisabled(!!p.disabled);
    setPurchaseLink(p.purchaseLink || '');
    setShippingMethod(p.shippingMethod || '');
    setReturnsPolicy(p.returnsPolicy || '');
  setPrice(p.price ?? 0);
  setPricing({ pricingMethod: p.pricingMethod || 'manual', costPrice: p.costPrice ?? '', profitMargin: p.profitMargin ?? '', costSource: p.costSource || 'admin' });
  const normalizedMain = normalizeToWebPath(p.image || '', folder) || '';
  setImage(normalizedMain);
    setImageFolderName(folder);
    setImageC([]); // reset pending local files

    const gallery = Array.isArray(p.imageB) ? p.imageB.map((it) => ({ ...it, src: normalizeToWebPath(it?.src, folder) })) : [];
    // de-dupe by src
    const map = new Map();
    gallery.forEach((it) => it?.src && map.set(it.src, it));
    setImageB(Array.from(map.values()));

    const variants = Array.isArray(p.colorsVariants)
      ? p.colorsVariants.map((v) => ({
          ...v,
          // normalize gallery image path
          src: normalizeToWebPath(v?.src, folder),
          // ensure price is either a finite number or empty string for UI
          price: (typeof v?.price === 'number' && !Number.isNaN(v.price) && v.price >= 0)
            ? v.price
            : '',
          unavailable: !!v?.unavailable,
        }))
      : [];
  setColorsVariants(variants);

  // New variant fields
  setVariantProps(Array.isArray(p.variantProps) ? p.variantProps : []);
  setSkus(Array.isArray(p.skus) ? p.skus : []);

  // Hydrate external main URL toggle based on image value
  const isExternal = isValidHttpUrl(normalizedMain);
  setUseExternalMain(isExternal);
  setExternalMainUrl(isExternal ? normalizedMain : '');

    setVideo(p.video || '');
    setBrand(p.brand || '');
  setCategory(p.category || '');
  // taxonomy prefill
  setSelectedCategoryId(p.categoryId || null);
  setPrefillCategoryPath(Array.isArray(p.categoryPath) ? p.categoryPath : []);
    setCountInStock(p.countInStock ?? 0);

    // Description to EditorState
    const html = p.description || '';
    try {
      const blocksFromHtml = htmlToDraft(html);
      const { contentBlocks, entityMap } = blocksFromHtml || {};
      if (contentBlocks) {
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap || {});
        setDescription(EditorState.createWithContent(contentState));
      } else {
        setDescription(EditorState.createEmpty());
      }
    } catch {
      setDescription(EditorState.createEmpty());
    }

    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing omission of 'isValidHttpUrl', not part of this cleanup pass
  }, [imageFolderName, normalizeToWebPath]);

  // Effect A: Always fetch fresh details for this product id on mount/param change
  useEffect(() => {
    if (fetchedForIdRef.current === productId) return;
    setHydrated(false);
    fetchedForIdRef.current = productId;
    dispatch(fetchProductDetails(productId));
  }, [dispatch, productId]);

  // Effect B: Hydrate local state once per loaded product _id
  useEffect(() => {
    if (!productDetails || !productDetails._id) return;
    if (initializedForIdRef.current === productDetails._id) return;
    // Ensure the loaded details correspond to our current route id
    if (productDetails._id !== productId) return;
    initializedForIdRef.current = productDetails._id;
    hydrateFromProduct(productDetails);
    // Initialize baseline snapshot after hydration
    setTimeout(() => { try { initialSnapshotRef.current = buildSnapshot(); setIsDirty(false); } catch {} }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing omission of 'buildSnapshot', not part of this cleanup pass
  }, [productDetails, productId, hydrateFromProduct]);

  // Effect C: After a successful update, refetch fresh details and reset success flag
  useEffect(() => {
    if (successUpdate) {
      if (mountedRef.current) setHydrated(false);
      initializedForIdRef.current = null;
      dispatch(fetchProductDetails(productId));
      // Baseline refresh for normal saves (not exiting)
      try { if (mountedRef.current && !exitAfterSave) { initialSnapshotRef.current = buildSnapshot(); setIsDirty(false); } } catch {}
      // Do NOT navigate here when exitAfterSave — immediate nav happens in submitHandler
      dispatch(resetUpdateProduct());
    }
  }, [successUpdate, dispatch, productId, buildSnapshot, exitAfterSave]);

  // Avoid infinite spinner on fetch error by letting UI advance to error state
  useEffect(() => {
    if (errorDetails && !loadingDetails) {
      // Only allow UI to proceed if we have previously loaded values to show.
      // If we have no productDetails yet, keep hydrated=false so the screen shows
      // the "Unable to load" state instead of an empty form.
      if (productDetails && productDetails._id) {
        setHydrated(true);
      }
    }
  }, [errorDetails, loadingDetails, productDetails]);

  // If Redux already contains an error (e.g., navigating back to this screen),
  // still surface it in the modal even if there was no loading transition.
  const shownInitialDetailsErrorRef = useRef(false);
  useEffect(() => {
    if (!shownInitialDetailsErrorRef.current && errorDetails && !loadingDetails) {
      openErrorModal('Failed to load product', errorDetails);
      shownInitialDetailsErrorRef.current = true;
    }
    if (!errorDetails) {
      shownInitialDetailsErrorRef.current = false;
    }
  }, [errorDetails, loadingDetails, openErrorModal]);

  // Route server errors to the unified error modal (event-based so repeated identical messages still show)
  const prevLoadingDetailsRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevLoadingDetailsRef.current;
    const finished = wasLoading && !loadingDetails;
    if (finished && errorDetails) openErrorModal('Failed to load product', errorDetails);
    prevLoadingDetailsRef.current = loadingDetails;
  }, [loadingDetails, errorDetails, openErrorModal]);

  const prevLoadingUpdateRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevLoadingUpdateRef.current;
    const finished = wasLoading && !loadingUpdate;
    if (finished && errorUpdate) openErrorModal('Failed to save product', errorUpdate);
    prevLoadingUpdateRef.current = loadingUpdate;
  }, [loadingUpdate, errorUpdate, openErrorModal]);

  const shownInitialUpdateErrorRef = useRef(false);
  useEffect(() => {
    if (!shownInitialUpdateErrorRef.current && errorUpdate && !loadingUpdate) {
      openErrorModal('Failed to save product', errorUpdate);
      shownInitialUpdateErrorRef.current = true;
    }
    if (!errorUpdate) {
      shownInitialUpdateErrorRef.current = false;
    }
  }, [errorUpdate, loadingUpdate, openErrorModal]);

  useEffect(() => {
    if (uploadValidationError) openErrorModal('Upload error', uploadValidationError);
  }, [uploadValidationError, openErrorModal]);

  useEffect(() => {
    if (galleryUrlError) openErrorModal('Gallery URL error', galleryUrlError);
  }, [galleryUrlError, openErrorModal]);

  useEffect(() => {
    if (externalMainError) openErrorModal('Image URL error', externalMainError);
  }, [externalMainError, openErrorModal]);

  const prevLoadingImageUploadRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevLoadingImageUploadRef.current;
    const finished = wasLoading && !loadingImageUpload;
    if (finished && errorImageUpload) openErrorModal('Image upload failed', errorImageUpload);
    prevLoadingImageUploadRef.current = loadingImageUpload;
  }, [loadingImageUpload, errorImageUpload, openErrorModal]);

  const shownInitialImageUploadErrorRef = useRef(false);
  useEffect(() => {
    if (!shownInitialImageUploadErrorRef.current && errorImageUpload && !loadingImageUpload) {
      openErrorModal('Image upload failed', errorImageUpload);
      shownInitialImageUploadErrorRef.current = true;
    }
    if (!errorImageUpload) {
      shownInitialImageUploadErrorRef.current = false;
    }
  }, [errorImageUpload, loadingImageUpload, openErrorModal]);

  useEffect(() => {
    if (localUploadError) openErrorModal('Request failed', localUploadError);
  }, [localUploadError, openErrorModal]);

  useEffect(() => {
    if (galleryError) openErrorModal('Gallery error', galleryError);
  }, [galleryError, openErrorModal]);

  useEffect(() => {
    if (deleteError) openErrorModal('Delete error', deleteError);
  }, [deleteError, openErrorModal]);

  useEffect(() => {
    if (errorImportReviews) openErrorModal('Import reviews failed', errorImportReviews);
  }, [errorImportReviews, openErrorModal]);

  useEffect(() => {
    if (successImportReviews) {
      const result = successImportReviews; // Backend response
      const skipped = result?.skippedDuplicates || 0;
      const imported = result?.imported || 0;

      let title = 'Import Complete';
      let message = '';

      if (imported > 0 && skipped > 0) {
        // Some imported, some skipped
        title = 'Import Completed with Duplicates';
        message = `Successfully imported ${imported} new review(s). ${skipped} duplicate review(s) were skipped.`;
      } else if (imported > 0 && skipped === 0) {
        // All imported successfully
        message = `Successfully imported ${imported} review(s). No duplicates found.`;
      } else if (imported === 0 && skipped > 0) {
        // All were duplicates
        title = 'No New Reviews Imported';
        message = `All ${skipped} review(s) were duplicates and were skipped. No new reviews added.`;
      } else {
        // Edge case: nothing imported, nothing skipped
        message = 'Import completed but no reviews were processed.';
      }

      openErrorModal(title, message);
      
      // Clean up state
      setRawReviewHtml('');
      setReviewParseWarnings([]);
      setParsedReviews([]);
      setImportLimit('');
      setSelectedReviewIdx(new Set());
      // Refresh product to show imported reviews
      dispatch(fetchProductDetails(productId));
      dispatch(resetImportReviews());
    }
  }, [successImportReviews, dispatch, productId, openErrorModal]);

  // Avoid gating on hydration when update fails
  useEffect(() => {
    if (errorUpdate) {
      setHydrated(true);
    }
  }, [errorUpdate]);

  // Fallback sync: when a leaf categoryId is selected and category store has it,
  // ensure local `category` name and `prefillCategoryPath` reflect that selection.
  useEffect(() => {
    if (!selectedCategoryId) return;
    const n = categoryById && categoryById[selectedCategoryId];
    if (!n) return;
    const pathSlugs = [
      ...((Array.isArray(n.ancestors) ? n.ancestors : []).map((a) => a?.slug).filter(Boolean)),
      n.slug,
    ].filter(Boolean);
    const nextName = n.name || '';
    if ((category || '') !== nextName) setCategory(nextName);
    if (JSON.stringify(prefillCategoryPath) !== JSON.stringify(pathSlugs)) setPrefillCategoryPath(pathSlugs);
    // Keep isLeaf in sync when store resolves it
    setSelectedIsLeaf(!!n.isLeaf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing omission of 'category'/'prefillCategoryPath', not part of this cleanup pass
  }, [selectedCategoryId, categoryById]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Abort in-flight upload on unmount
      if (uploadAbortRef.current) {
        try { uploadAbortRef.current.abort(); } catch (_) {}
      }
  // Reset guards so the next mount fetches and hydrates fresh
  fetchedForIdRef.current = null;
  initializedForIdRef.current = null;
      mountedRef.current = false;
    };
  }, []);

  // Track dirty state by comparing snapshots whenever relevant fields change
  useEffect(() => {
    const current = buildSnapshot();
    const initial = initialSnapshotRef.current;
    setIsDirty(initial ? !isEqualSnapshot(current, initial) : false);
  }, [buildSnapshot, isEqualSnapshot]);

  // Set up navigation blocking for in-app route changes
  useEffect(() => {
    try { if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; } } catch {}
    if (!history || typeof history.block !== 'function') return;
    unblockRef.current = history.block((location, action) => {
      if (isDirty) {
        setPendingNav({ location, action });
        setPendingNavAction(action);
        setShowUnsavedModal(true);
        return false;
      }
      return undefined;
    });
    return () => { try { if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; } } catch {} };
  }, [history, isDirty]);

  // Native prompt on hard refresh/close
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => {
    if (successImageUpload && !loadingImageUpload && !uploadingFiles) {
      setShowUploadImageModal(true);
    }
  }, [successImageUpload, loadingImageUpload, uploadingFiles]);

  // Long-press handlers for main image deletion
  const startLongPressMainSelect = (selectedSrc) => {
    try { if (longPressRef.current.timer) clearTimeout(longPressRef.current.timer); } catch (_) {}
    if (!selectedSrc) return;
    longPressRef.current.src = selectedSrc;
    longPressRef.current.timer = setTimeout(() => {
      setDeleteError('');
      setDeleteContext({ type: 'single', srcs: [selectedSrc] });
      setShowDeleteConfirm(true);
    }, 600);
  };
  const cancelLongPressMainSelect = () => {
    try { if (longPressRef.current.timer) clearTimeout(longPressRef.current.timer); } catch (_) {}
    longPressRef.current.timer = null;
    longPressRef.current.src = '';
  };

  // Gallery selection helpers
  const toggleSelectToDelete = (src) => {
    setSelectedToDelete((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src); else next.add(src);
      return next;
    });
  };
  const selectAllInGallery = () => {
    setSelectedToDelete(new Set((Array.isArray(galleryItems) ? galleryItems : []).map((i) => i?.src).filter(Boolean)));
  };
  const clearSelection = () => setSelectedToDelete(new Set());
  const openDeleteConfirm = (ctx) => { setDeleteError(''); setDeleteContext(ctx); setShowDeleteConfirm(true); };

  const uploadFileHandler = (e) => {
    const files = Array.from(e.target.files || []);
    const MAX_MB = 5;
    const MAX_BYTES = MAX_MB * 1024 * 1024;
    const allowed = new Set(['image/jpeg', 'image/png']);

    const accepted = [];
    let invalidCount = 0;

    for (const f of files) {
      const okType = allowed.has(f.type);
      const okSize = f.size <= MAX_BYTES;
      if (okType && okSize) {
        accepted.push(f);
      } else {
        invalidCount++;
      }
    }

    setImageC(accepted);
    if (invalidCount > 0) {
      setUploadValidationError(`Only JPG/PNG up to ${MAX_MB}MB per file. Skipped ${invalidCount} invalid file(s).`);
    } else {
      setUploadValidationError('');
    }
  };

  // First upload files to /api/upload/:folderName, then save returned srcs on the product
  const uploadImagesHandler = async () => {
    try {
      setLocalUploadError('');
      if (!imageC || imageC.length === 0) {
        setUploadValidationError('Please choose at least one valid file to upload.');
        return;
      }
      const folder = imageFolderName || 'Undefined';

      const formData = new FormData();
      Array.from(imageC).forEach((file) => formData.append('files', file));

      setUploadingFiles(true);
      const controller = new AbortController();
      uploadAbortRef.current = controller;

      const { data: uploaded } = await axios.post(
        `/api/upload/${encodeURIComponent(folder)}`,
        formData,
        { ...uploadAuthConfig, signal: controller.signal }
      );

      // Immediately reflect uploaded items in local UI
      setImageB((prev) => {
        const map = new Map();
        (Array.isArray(prev) ? prev : []).forEach((it) => it?.src && map.set(it.src, it));
        (Array.isArray(uploaded) ? uploaded : []).forEach((it) => it?.src && map.set(it.src, it));
        return Array.from(map.values());
      });
      if (!image && Array.isArray(uploaded) && uploaded.length > 0) {
        setImage(uploaded[0]?.src || '');
      }

      uploadAbortRef.current = null;
      setUploadingFiles(false);

      await dispatch(
        uploadProductImage({
          _id: productId,
          imageFolderName: folder,
          imageB: uploaded, // array of { src }
        })
      );
    } catch (err) {
      // Distinguish cancellation vs other errors
      if (uploadAbortRef.current) {
        // If current ref exists, clear it now
        uploadAbortRef.current = null;
      }
      setUploadingFiles(false);
      const canceled = err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.name === 'AbortError';
      const msg = canceled ? 'Upload canceled.' : getErrorMessage(err, 'Upload failed.');
      // Open modal directly so repeat-identical errors always show.
      openErrorModal(canceled ? 'Upload canceled' : 'Upload failed', msg);
      setLocalUploadError(msg);
      console.error('Upload failed', err);
    }
  };

  const handleConfirmClose = () => {
    setShowUploadImageModal(false);
    dispatch(resetImageUpload());
    setLocalUploadError('');
    setUploadValidationError('');
    setImageC([]);
    const input = document.getElementById('image-file');
    if (input) input.value = null;
  };

  // Confirm deletion using authenticated thunk to ensure Authorization header is sent
  const onConfirmDelete = async () => {
    if (deleteBusy) return;
    let skipDirectUpdate = false;
    try {
      setDeleteBusy(true);
      setDeleteError('');
      const deletedSrcs = Array.isArray(deleteContext?.srcs) ? deleteContext.srcs.filter(Boolean) : [];
      if (deletedSrcs.length === 0) { setShowDeleteConfirm(false); setDeleteBusy(false); return; }
      const nextImageB = (Array.isArray(imageB) ? imageB : []).filter((it) => it?.src && !deletedSrcs.includes(it.src));
      let nextImage = image;
      if (image && deletedSrcs.includes(image)) { nextImage = nextImageB[0]?.src || ''; }
      const nextVariants = (Array.isArray(colorsVariants) ? colorsVariants : []).map((v) => v?.src && deletedSrcs.includes(v.src) ? { ...v, src: '' } : v);

      // Use the deleteImages thunk which performs server deletion + product update with auth
      // Only send server-local paths; skip http(s) external URLs
      const serverDeletable = deletedSrcs.filter((s) => {
        try { const u = new URL(String(s)); return !(u.protocol === 'http:' || u.protocol === 'https:'); } catch { return true; }
      });
      if (imageFolderName && serverDeletable.length > 0) {
        try {
          await dispatch(deleteImages({
            productId,
            folder: imageFolderName,
            srcs: serverDeletable,
            nextImage,
            nextImageB,
            nextVariants,
          })).unwrap();
          skipDirectUpdate = true;
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'File deletion failed on server.';
          setDeleteError(msg);
        }
      }

      if (!skipDirectUpdate) {
        await dispatch(updateProduct({ _id: productId, image: nextImage, imageFolderName, imageB: nextImageB, colorsVariants: nextVariants })).unwrap();
      }

      // Update local UI state
      setImage(nextImage);
      setImageB(nextImageB);
      setColorsVariants(nextVariants);
      clearSelection();
      setDeleteMode(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      const msg = getErrorMessage(err, 'Failed to delete images.');
      setDeleteError(msg);
      openErrorModal('Delete error', msg);
    } finally {
      setDeleteBusy(false);
    }
  };

  // Orchestrate everything in one click: optional upload -> update (no redirect)
  const submitHandler = async (e) => {
    // Make event optional so we can call this from onClick
    try {
      e?.preventDefault?.();
      setLocalUploadError('');
      setSubmitting(true);
      const resolvedPricing = resolveProductPricing({ ...pricing, price, skus, colorsVariants });

      // Ensure Draft editor state is flushed before reading
      await Promise.resolve();

      const descriptionHtml = draftToHtml(convertToRaw(description.getCurrentContent())) || '';

  // If there are pending files, upload them first and merge into imageB
      let uploaded = [];
      let finalImageB = imageB;
      let finalImage = image;

      if (imageC && imageC.length > 0) {
        const folder = imageFolderName || 'Undefined';
        const formData = new FormData();
        Array.from(imageC).forEach((file) => formData.append('files', file));

        const { data } = await axios.post(`/api/upload/${encodeURIComponent(folder)}`, formData, uploadAuthConfig);
        uploaded = Array.isArray(data) ? data : [];

        const map = new Map();
        (Array.isArray(imageB) ? imageB : []).forEach((it) => it?.src && map.set(it.src, it));
        uploaded.forEach((it) => it?.src && map.set(it.src, it));
        finalImageB = Array.from(map.values());
        setImageB(finalImageB);

        if (!finalImage && uploaded.length > 0) {
          finalImage = uploaded[0]?.src || '';
          setImage(finalImage);
        }
      }

      // Ensure gallery includes any images that are currently applied on the form (main + variants)
      try {
        const map = new Map();
        (Array.isArray(finalImageB) ? finalImageB : []).forEach((it) => it?.src && map.set(it.src, it));
        const addIf = (s) => {
          const key = (s || '').trim();
          if (!key) return;
          if (!map.has(key)) map.set(key, { src: key });
        };
        addIf(finalImage);
        (Array.isArray(colorsVariants) ? colorsVariants : []).forEach((v) => addIf(v?.src));
        finalImageB = Array.from(map.values());
        setImageB(finalImageB);
      } catch {}

      // Determine final categoryId: only send a categoryId if the selection is a leaf.
      const effectiveCategoryId = (selectedIsLeaf ? selectedCategoryId : (productDetails?.categoryId || null));
      const payload = {
        _id: productId,
        name,
        disabled,
        purchaseLink,
        price,
        image: finalImage,
        imageFolderName,
        imageB: finalImageB,
        colorsVariants,
        variantProps,
        skus,
        video,
        brand,
        shippingMethod,
        returnsPolicy,
        // Always send trimmed category string (hydrated or newly selected leaf)
        category: (category || '').trim(),
        description: descriptionHtml,
        countInStock,
        ...resolvedPricing,
      };
        if (effectiveCategoryId) payload.categoryId = effectiveCategoryId;
        if (prefillCategoryPath && prefillCategoryPath.length) payload.categoryPath = prefillCategoryPath;
      const updated = await dispatch(updateProduct(payload)).unwrap();
      // Track the saved product for canonical path resolution
      try { lastSavedProductRef.current = updated; } catch {}

      // Optimistically hydrate UI with the updated product
      try { hydrateFromProduct(updated); } catch {}

      // If Save-and-Exit was requested via modal, navigate immediately based on intent
      if (exitAfterSave) {
        let finalTarget = null;
        if (exitIntent === 'backToList') {
          finalTarget = '/admin/productlist';
        } else {
          finalTarget = `/product/${updated.slug || updated._id}`;
        }
        try { if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; } } catch {}
        try {
          // Use the blocked action preference when present
          pendingNavAction === 'REPLACE' ? history.replace(finalTarget) : history.push(finalTarget);
        } catch {}
        if (mountedRef.current) {
          setShowUnsavedModal(false);
          setExitAfterSave(false);
          setExitTarget(null);
          setPendingNav(null);
          setExitIntent('other');
          // Reset dirty state baseline after save
          try { initialSnapshotRef.current = buildSnapshot(); setIsDirty(false); } catch {}
        }
        return; // prevent showing toast in this immediate-exit path
      } else {
        // Show toast only if we're not exiting after save and component still mounted
        if (mountedRef.current) {
          setShowSavedToast(true);
        }
      }
    } catch (err) {
      const msg = getErrorMessage(err, 'Update failed.');
      // Open modal directly so repeat-identical errors always show.
      openErrorModal('Failed to save product', msg);
      setLocalUploadError(msg);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  // Parse pasted external HTML to populate fields
  const handleParsePastedHtml = () => {
    if (!rawHtml || typeof rawHtml !== 'string' || rawHtml.trim().length === 0) return;
    try {
      setParsing(true);
      const { title, price: parsedPrice, priceSource, variants, variantProps: parsedVariantProps, skus: parsedSkus, warnings, gallery, video: parsedVideo, suggestedFolderName } = parseAliHtml(rawHtml);
      if (title) setName(title);
      if (parsedPrice != null && Number.isFinite(parsedPrice)) setPrice(parsedPrice);
      if (parsedVideo && typeof parsedVideo === 'string') setVideo(parsedVideo);
      // Map parsed variants to local shape { name, src, price, unavailable }
      if (Array.isArray(variants) && variants.length) {
        const mapped = variants.map((v) => ({
          name: (v?.name || '').trim(),
          src: (v?.image || '').trim(),
          price: '',
          unavailable: !!v?.disabled,
        })).filter((v) => v.name && v.src);
        if (mapped.length) setColorsVariants(mapped);
      }
      // New variant system from parsed props/skus
      if (Array.isArray(parsedVariantProps) && parsedVariantProps.length) {
        setVariantProps(parsedVariantProps);
      }
      if (Array.isArray(parsedSkus)) {
        setSkus(parsedSkus);
      }
      // If parser suggested a folder and none is set, adopt it
      if (suggestedFolderName && !imageFolderName) {
        setImageFolderName(suggestedFolderName);
      }
      // Merge gallery images into imageB and set main image if absent
      if (Array.isArray(gallery) && gallery.length) {
        setImageB((prev) => {
          const map = new Map();
          (Array.isArray(prev) ? prev : []).forEach((it) => it?.src && map.set(it.src, it));
          gallery.forEach((src) => src && map.set(src, { src }));
          return Array.from(map.values());
        });
        if (!image) {
          const first = gallery[0];
          if (first) setImage(first);
        }
      }
      setPriceSourceBadge(priceSource);
      setParseWarnings(Array.isArray(warnings) ? warnings : []);
    } finally {
      setParsing(false);
    }
  };

  const handleParseReviewHtml = () => {
    if (!rawReviewHtml || typeof rawReviewHtml !== 'string' || rawReviewHtml.trim().length === 0) return;
    try {
      setParsingReviews(true);
      const { reviews, warnings } = parseAliReviewHtml(rawReviewHtml);
      setParsedReviews(Array.isArray(reviews) ? reviews : []);
      setImportLimit('');
      setSelectedReviewIdx(new Set());
      setReviewParseWarnings(Array.isArray(warnings) ? warnings : []);
      if ((!reviews || reviews.length === 0) && Array.isArray(warnings) && warnings.length === 0) {
        setReviewParseWarnings(['No reviews found']);
      }
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to parse review HTML.');
      setReviewParseWarnings([msg]);
      openErrorModal('Parse reviews failed', msg);
    } finally {
      setParsingReviews(false);
    }
  };

  // Helper: Check if two reviews are exact duplicates (all fields match)
  // Uses date-only comparison (YYYY-MM-DD) to avoid millisecond precision issues
  const areReviewsIdentical = useCallback((review1, review2) => {
    // Compare primitive fields
    if (
      review1.name !== review2.name ||
      review1.rating !== review2.rating ||
      (review1.comment || '') !== (review2.comment || '')
    ) {
      return false;
    }

    // Compare createdAt timestamps - ONLY compare DATE portion (YYYY-MM-DD), ignore time
    const date1 = review1.createdAt
      ? new Date(review1.createdAt).toISOString().split('T')[0]
      : null;
    const date2 = review2.createdAt
      ? new Date(review2.createdAt).toISOString().split('T')[0]
      : null;
    if (date1 !== date2) {
      return false;
    }

    // Compare variant (normalize empty/undefined to empty string)
    const variant1 = review1.variant || '';
    const variant2 = review2.variant || '';
    if (variant1 !== variant2) {
      return false;
    }

    // Compare images arrays (order-independent, sorted comparison)
    const images1 = Array.isArray(review1.images) ? [...review1.images].sort() : [];
    const images2 = Array.isArray(review2.images) ? [...review2.images].sort() : [];
    if (images1.length !== images2.length) {
      return false;
    }
    for (let i = 0; i < images1.length; i++) {
      if (images1[i] !== images2[i]) {
        return false;
      }
    }

    return true; // All fields match exactly
  }, []);

  // Helper: Remove duplicates within the batch and against existing reviews
  const filterDuplicateReviews = useCallback((batch, existing) => {
    const unique = [];
    const seenInBatch = new Set();
    
    for (const review of batch) {
      // Create a signature for quick duplicate detection within batch
      const signature = JSON.stringify({
        name: review.name,
        rating: review.rating,
        comment: review.comment || '',
        createdAt: review.createdAt
          ? new Date(review.createdAt).toISOString().split('T')[0]
          : null,
        variant: review.variant || '',
        images: Array.isArray(review.images) ? [...review.images].sort() : [],
      });

      // Skip if we've seen this exact review in the current batch
      if (seenInBatch.has(signature)) {
        continue;
      }

      // Skip if this review already exists in the product
      const existsInProduct = existing.some((existingReview) =>
        areReviewsIdentical(review, existingReview)
      );

      if (!existsInProduct) {
        unique.push(review);
        seenInBatch.add(signature);
      }
    }

    return unique;
  }, [areReviewsIdentical]);

  const handleImportParsedReviews = async () => {
    const list = Array.isArray(effectiveReviews) ? effectiveReviews : [];
    if (!productId || list.length === 0) return;

    try {
      // ALWAYS fetch fresh product data from database before checking duplicates
      // This ensures we're checking against the most current reviews in the database
      await dispatch(fetchProductDetails(productId)).unwrap();
      
      // Now get the freshly loaded existing reviews from the product
      const existingReviews = productDetails?.reviews || [];

      // Filter out duplicates (both within batch and against existing database reviews)
      const newReviews = filterDuplicateReviews(list, existingReviews);

      // Calculate duplicate counts
      const totalDuplicates = list.length - newReviews.length;
      
      // Inform user about duplicates being skipped (if any)
      if (totalDuplicates > 0 && newReviews.length === 0) {
        // All reviews are duplicates - don't send to backend
        openErrorModal(
          'All Reviews Already Exist',
          `All ${list.length} review(s) in this batch already exist in the database. No new reviews to import.`
        );
        // Clean up state
        setParsedReviews([]);
        setRawReviewHtml('');
        setImportLimit('');
        setSelectedReviewIdx(new Set());
        return;
      }
      
      if (totalDuplicates > 0 && newReviews.length > 0) {
        // Some duplicates, but some new reviews to import
        openErrorModal(
          'Duplicate Reviews Detected',
          `Found ${totalDuplicates} duplicate review(s) in the database. These will be skipped. Proceeding to import ${newReviews.length} unique review(s).`
        );
      }

      // Proceed with import of unique reviews
      await dispatch(importProductReviews({ productId, reviews: newReviews })).unwrap();
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to import reviews or check database.');
      openErrorModal('Import Failed', msg);
    }
  };

  const toggleSelectParsedReviewIdx = (idx) => {
    setSelectedReviewIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const deleteSelectedParsedReviews = () => {
    if (!selectedReviewIdx || selectedReviewIdx.size === 0) return;
    setParsedReviews((prev) => (Array.isArray(prev) ? prev.filter((_, idx) => !selectedReviewIdx.has(idx)) : []));
    setSelectedReviewIdx(new Set());
  };

  const addColorsVariants = () => {
    setColorsVariants([...colorsVariants, { name: '', src: '', price: '', unavailable: false }]);
  };

  const removeColorsVariants = (index) => {
    const newColorsVariants = colorsVariants.filter((_, i) => i !== index);
    setColorsVariants(newColorsVariants);
  };

  const handleColorsVariantsChange = (index, key, value) => {
    const newColorsVariants = [...colorsVariants];
    if (key === 'price') {
      // Allow empty for "inherit base"; otherwise coerce to non-negative numeric string
      const raw = String(value ?? '').trim();
      if (raw === '') {
        newColorsVariants[index].price = '';
      } else {
        const n = Number(raw);
        newColorsVariants[index].price = Number.isFinite(n) && n >= 0 ? n : '';
      }
    } else {
      newColorsVariants[index][key] = value;
    }
    setColorsVariants(newColorsVariants);
  };

  // Convert legacy colorsVariants to new variantProps/skus once
  const convertLegacyVariants = () => {
    const list = Array.isArray(colorsVariants) ? colorsVariants.filter(v => v && (v.name || v.src)) : [];
    if (list.length === 0) return;
    const propId = 'color';
    const newValues = list.map((v, i) => ({
      id: (v.name || v.src || `opt-${i}`).toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '').slice(0, 32) || `opt-${i}`,
      name: v.name || `Option ${i + 1}`,
      image: v.src || '',
      hot: false,
      disabled: !!v.unavailable,
    }));
    const newProps = [{ id: propId, name: 'Color', type: 'image', displayOrder: 0, values: newValues }];
    const newSkus = newValues.map((val) => {
      const legacy = list.find(cv => (cv?.name || '').trim() === (val.name || '').trim()) || {};
      return {
        key: { [propId]: val.id },
        costPrice: legacy.costPrice,
        useBasePrice: legacy.useBasePrice,
        price: (legacy.price === undefined || legacy.price === null || legacy.price === '') ? undefined : Number(legacy.price),
        countInStock: Number(countInStock) || 0,
        image: legacy.src || '',
        unavailable: !!legacy.unavailable,
      };
    });
    setVariantProps(newProps);
    setSkus(newSkus);
  };

  // Open gallery modal and load files from /uploads/<imageFolderName>
  const openGallery = async (target = { type: 'main', index: null }) => {
    setGalleryTarget(target);
    if (!imageFolderName) {
      setGalleryError('Please set an Image Gallery Folder Name first.');
      setShowGallery(true);
      return;
    }
    try {
      setGalleryError('');
      setGalleryLoading(true);
      setShowGallery(true);

      const normalizedFolder = (imageFolderName || '').trim();
      const canQueryServerFolder =
        normalizedFolder.length > 0 &&
        !/[\\/]/.test(normalizedFolder) &&
        /^[a-z0-9 _'()&,.-]+$/i.test(normalizedFolder);

      let serverItems = [];
      if (canQueryServerFolder) {
        const { data } = await axios.get(`/api/upload/list/${encodeURIComponent(normalizedFolder)}`, uploadAuthConfig);
        serverItems = Array.isArray(data) ? data : [];
      }

      // Merge server items with any URLs currently in the product gallery (imageB)
      const merged = new Map();
      (serverItems || []).forEach((it) => it?.src && merged.set(it.src, { src: it.src }));
      (Array.isArray(imageB) ? imageB : []).forEach((it) => it?.src && merged.set(it.src, { src: it.src }));
      setGalleryItems(Array.from(merged.values()));
    } catch (e) {
      const msg = getErrorMessage(e, 'Failed to load folder contents.');
      setGalleryError(msg);
      openErrorModal('Gallery error', msg);
    } finally {
      setGalleryLoading(false);
    }
  };

  const closeGallery = () => {
    setShowGallery(false);
    setGalleryItems([]);
    setGalleryError('');
    setGalleryLoading(false);
    setGalleryTarget({ type: 'main', index: null });
  };

  const selectGalleryImage = (src) => {
    if (galleryTarget?.type === 'variant' && galleryTarget.index !== null) {
      const idx = galleryTarget.index;
      setColorsVariants((prev) => {
        const copy = [...prev];
        if (copy[idx]) copy[idx] = { ...copy[idx], src };
        return copy;
      });
    } else {
      setImage(src || '');
    }
    // Ensure the chosen image (from server gallery) is part of the product's gallery list
    if (src) ensureInGallery(src);
    closeGallery();
  };

  // Unsaved changes modal actions
  const handleUnsavedSaveAndExit = () => {
    if (pendingNav?.location) {
      setExitAfterSave(true);
      // Determine intent based on the blocked target path
      const loc = pendingNav.location;
      const targetPath = typeof loc === 'string' ? loc : (loc && (loc.pathname || loc.path)) || '';
      if (pendingNavAction === 'POP') {
        // Browser back/forward: treat as back to list per requirements
        setExitIntent('backToList');
        setExitTarget('/admin/productlist');
      } else if (targetPath.startsWith('/product')) {
        setExitIntent('viewProduct');
        // Best-effort canonical product path before save completes
        const guessedPath = productDetails?.slug ? `/product/${productDetails.slug}` : (productDetails?._id ? `/product/${productDetails._id}` : null);
        setExitTarget(guessedPath || pendingNav.location);
      } else if (targetPath === '/admin/productlist') {
        setExitIntent('backToList');
        setExitTarget('/admin/productlist');
      } else {
        setExitIntent('other');
        setExitTarget(pendingNav.location);
      }
      // Trigger the standard save flow
      submitHandler();
    }
  };

  const handleUnsavedExitWithoutSave = () => {
    if (pendingNav?.location) {
      try { if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; } } catch {}
      try { pendingNav.action === 'REPLACE' ? history.replace(pendingNav.location) : history.push(pendingNav.location); } catch {}
      setShowUnsavedModal(false);
      setPendingNav(null);
    }
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
    setPendingNav(null);
  };

  // Helper list of gallery option values and whether current image exists among them
  const imageSource = (Array.isArray(imageB) && imageB.length)
    ? imageB
    : (Array.isArray(product?.imageB) && product.imageB.length)
      ? product.imageB
      : (Array.isArray(productDetails?.imageB) ? productDetails.imageB : []);
  const imageOptions = Array.isArray(imageSource) ? imageSource.map((img) => img?.src).filter(Boolean) : [];
  const hasCurrentImageOption = !!image && imageOptions.includes(image);
  const hasGallery = imageOptions.length > 0;

  // Stable path to view the public product page (uses slug when available, falls back to ObjectId)
  const viewProductPath = `/product/${productDetails?.slug || productId}`;

  return (
    <>
      <Link to='/admin/productlist' className='btn btn-light my-3'>
        Product List
      </Link>
      <Link to={viewProductPath} className='btn btn-outline-primary my-3 ml-2'>
        View Product
      </Link>
      <FormContainer>
        <h1>Edit Product</h1>
        {loadingUpdate && <Loader />}
        {(!hydrated && (loadingDetails || (!loadingDetails && !errorDetails))) ? (
          <Loader />
        ) : (!hydrated && errorDetails) ? (
          <div className='text-muted'>Unable to load product.</div>
        ) : (
          <>
            {errorDetails && hydrated && (
              <div className='text-muted' style={{ fontSize: 12, marginTop: 8 }}>
                Couldn’t refresh product details. Showing last loaded values.
              </div>
            )}
            {/* Remove onSubmit and trigger via explicit onClick for one-click reliability */}
            <Form>
            <Form.Group controlId='disabledToggle' className='pt-2'>
              <Form.Check
                type='checkbox'
                id='product-disabled'
                label='Disable product (hide from store)'
                checked={disabled}
                onChange={(e) => setDisabled(e.target.checked)}
              />
              <div className='text-muted' style={{ fontSize: 12 }}>
                Disabled products don’t appear in the store, can’t be opened, and can’t be added to cart. Admins can still edit them.
              </div>
            </Form.Group>
            <Form.Group controlId='name' className='pt-2'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='name'
                placeholder='Enter name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='purchaseLink' className='pt-5'>
              <Form.Label>PurchaseLink</Form.Label>
              <Form.Control
                type='text'
                placeholder='Purchase Link'
                value={purchaseLink}
                onChange={(e) => setPurchaseLink(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <ProductPricingFields value={{ ...pricing, price, skus, colorsVariants }} onChange={({ skus: nextSkus, colorsVariants: nextColors, ...patch }) => {
              setPricing(prev => ({ ...prev, ...patch }));
              if (nextSkus) setSkus(nextSkus);
              if (nextColors) setColorsVariants(nextColors);
            }} />
            <Form.Group controlId='price' className='pt-5'>
              <Form.Label>Price</Form.Label>
              <Form.Control
                type='number'
                placeholder='Enter price'
                readOnly={pricing.pricingMethod === 'margin'}
                value={pricing.pricingMethod === 'margin' ? (() => { try { return calculateMarginPrice(pricing.costPrice, pricing.profitMargin); } catch (_) { return ''; } })() : price}
                onChange={(e) => setPrice(e.target.value)}
              ></Form.Control>
              {priceSourceBadge && (
                <div className='text-muted mt-1' style={{ fontSize: 12 }}>
                  Price source: {priceSourceBadge === 'original' ? 'Original (Compare-at)' : priceSourceBadge === 'current' ? 'Current' : 'Unknown'}
                </div>
              )}
            </Form.Group>

              {/* Paste HTML to auto-fill (admin-only screen) */}
              <Form.Group controlId='pastedHtml' className='pt-5'>
                <Form.Label>Paste External HTML (Auto-fill)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={6}
                  placeholder='Paste product page HTML here'
                  value={rawHtml}
                  onChange={(e) => setRawHtml(e.target.value)}
                />
                <div className='mt-2 d-flex align-items-center' style={{ gap: 8 }}>
                  <Button variant='outline-primary' onClick={handleParsePastedHtml} disabled={parsing || !rawHtml.trim()}>
                    {parsing ? 'Parsing…' : 'Parse & Fill'}
                  </Button>
                  {Array.isArray(parseWarnings) && parseWarnings.length > 0 && (
                    <span className='text-muted' style={{ fontSize: 12 }}>
                      {parseWarnings.join(' • ')}
                    </span>
                  )}
                </div>
                <div className='text-muted' style={{ fontSize: 12, marginTop: 6 }}>
                  Uses Original (Compare‑at) price when available; falls back to Current.
                </div>
              </Form.Group>

              <Form.Group controlId='pastedReviewHtml' className='pt-5'>
                <Form.Label>Paste Review HTML (Import Reviews)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={6}
                  placeholder='Paste AliExpress review HTML here'
                  value={rawReviewHtml}
                  onChange={(e) => setRawReviewHtml(e.target.value)}
                />
                <div className='mt-2 d-flex align-items-center' style={{ gap: 8 }}>
                  <Button
                    variant='outline-primary'
                    onClick={handleParseReviewHtml}
                    disabled={parsingReviews || !rawReviewHtml.trim()}
                  >
                    {parsingReviews ? 'Parsing…' : 'Parse Reviews'}
                  </Button>
                  <Button
                    variant='outline-danger'
                    onClick={deleteSelectedParsedReviews}
                    disabled={selectedReviewIdx.size === 0}
                  >
                    Delete selected ({selectedReviewIdx.size})
                  </Button>
                  <div className='d-flex align-items-center' style={{ gap: 6 }}>
                    <span className='text-muted' style={{ fontSize: 12 }}>Import first</span>
                    <Form.Control
                      type='number'
                      min='1'
                      placeholder='All'
                      value={importLimit}
                      onChange={(e) => setImportLimit(e.target.value)}
                      style={{ width: 100 }}
                      aria-label='Import first'
                    />
                  </div>
                  <Button
                    variant='primary'
                    onClick={handleImportParsedReviews}
                    disabled={loadingImportReviews || effectiveReviews.length === 0}
                  >
                    {loadingImportReviews ? 'Importing…' : `Import Reviews (${effectiveReviews.length})`}
                  </Button>
                  {Array.isArray(reviewParseWarnings) && reviewParseWarnings.length > 0 && (
                    <span className='text-muted' style={{ fontSize: 12 }}>
                      {reviewParseWarnings.join(' • ')}
                    </span>
                  )}
                </div>

                {Array.isArray(parsedReviews) && parsedReviews.length > 0 && (
                  <Table striped bordered hover responsive size='sm' className='mt-3'>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Select</th>
                        <th>Name</th>
                        <th>Rating</th>
                        <th>Comment</th>
                        <th>Date</th>
                        <th>Variant</th>
                        <th>Images</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedReviews.map((r, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type='checkbox'
                              checked={selectedReviewIdx.has(idx)}
                              onChange={() => toggleSelectParsedReviewIdx(idx)}
                              aria-label={`Select review ${idx + 1}`}
                            />
                          </td>
                          <td>{(r?.name || '').trim() || 'AliExpress Shopper'}</td>
                          <td>
                            <Rating value={r?.rating || 0} />
                          </td>
                          <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(r?.comment || '').trim() || <span className='text-muted fst-italic'>(No comment)</span>}
                          </td>
                          <td>{r?.createdAt ? String(r.createdAt).substring(0, 10) : 'N/A'}</td>
                          <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r?.variant || 'N/A'}
                          </td>
                          <td>
                            {r?.images && r.images.length > 0 ? (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {r.images.slice(0, 3).map((img, i) => (
                                  <img
                                    key={i}
                                    src={img}
                                    alt=''
                                    style={{ 
                                      width: '40px', 
                                      height: '40px', 
                                      objectFit: 'cover', 
                                      borderRadius: '4px',
                                      border: '1px solid #ddd'
                                    }}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                ))}
                                {r.images.length > 3 && (
                                  <span className='text-muted small'>+{r.images.length - 3}</span>
                                )}
                              </div>
                            ) : (
                              <span className='text-muted'>None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                <div className='text-muted' style={{ fontSize: 12, marginTop: 6 }}>
                  Imports will preserve images, variant info, and review dates when available.
                </div>
              </Form.Group>

            <Form.Group controlId='shippingMethod' className='pt-5'>
              <Form.Label>Shipping Method</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter shipping method'
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='returnsPolicy' className='pt-5'>
              <Form.Label>Returns Policy</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter returns policy'
                value={returnsPolicy}
                onChange={(e) => setReturnsPolicy(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='imageFolderName' className='pt-5'>
              <Form.Label>Image Gallery Folder Name</Form.Label>
              <Form.Control
                type='text'
                value={imageFolderName}
                onChange={(e) => setImageFolderName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='image' className='pt-5'>
              <Form.Label>Image Gallery Files</Form.Label>
              <Form.File
                id='image-file'
                label='Choose File'
                name='multi-files'
                custom
                multiple
                onChange={uploadFileHandler}
              ></Form.File>
              <div className='mt-2 d-flex align-items-center' style={{ gap: 8 }}>
                <Button variant='primary' onClick={uploadImagesHandler} disabled={loadingImageUpload || uploadingFiles || !imageC || imageC.length === 0}>
                  {uploadingFiles ? 'Uploading…' : loadingImageUpload ? 'Saving…' : 'Upload Files'}
                </Button>
                {uploadingFiles && (
                  <Button variant='outline-danger' onClick={() => { try { uploadAbortRef.current?.abort(); } catch(_){} }}>
                    Cancel Upload
                  </Button>
                )}
              </div>
              {/* Add external image URL directly into gallery list */}
              <div className='mt-3'>
                <Form.Label>Or add external image URL to gallery</Form.Label>
                <div className='d-flex align-items-center' style={{ gap: 8 }}>
                  <Form.Control
                    type='text'
                    placeholder='https://example.com/image.jpg'
                    value={newGalleryUrl}
                    onChange={(e) => { setNewGalleryUrl(e.target.value); setGalleryUrlError(''); }}
                  />
                  <Button
                    variant='outline-primary'
                    onClick={() => {
                      const trimmed = (newGalleryUrl || '').trim();
                      if (!isValidHttpUrl(trimmed)) {
                        setGalleryUrlError('Please enter a valid http(s) URL.');
                        return;
                      }
                      // Prevent duplicates
                      const exists = (Array.isArray(imageB) ? imageB : []).some((it) => (it?.src || '').trim() === trimmed);
                      if (exists) {
                        setGalleryUrlError('That URL is already in the gallery.');
                        return;
                      }
                      const next = [...(Array.isArray(imageB) ? imageB : []), { src: trimmed }];
                      setImageB(next);
                      if (!image) setImage(trimmed);
                      setNewGalleryUrl('');
                      setGalleryUrlError('');
                    }}
                  >
                    Add URL
                  </Button>
                </div>
              </div>
            </Form.Group>

            <Form.Group controlId='image-main' className='pt-5'>
              <Form.Label>Image Main</Form.Label>
              <div className='mb-2 d-flex align-items-center' style={{ gap: 8 }}>
                <Form.Check
                  type='checkbox'
                  id='use-external-main'
                  label='Use external URL'
                  checked={useExternalMain}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseExternalMain(checked);
                    setExternalMainError('');
                    if (checked) {
                      const trimmed = (externalMainUrl || '').trim();
                      if (trimmed) setImage(trimmed);
                    }
                  }}
                />
                {useExternalMain && (
                  <>
                    <Form.Control
                      type='text'
                      placeholder='https://example.com/main-image.jpg'
                      value={externalMainUrl}
                      onChange={(e) => {
                        const val = e.target.value;
                        setExternalMainUrl(val);
                        setExternalMainError('');
                        setImage(val);
                      }}
                      style={{ maxWidth: 420 }}
                    />
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      onClick={() => {
                        const trimmed = (externalMainUrl || '').trim();
                        if (!isValidHttpUrl(trimmed)) {
                          setExternalMainError('Please enter a valid http(s) URL.');
                          return;
                        }
                        setImage(trimmed);
                        ensureInGallery(trimmed);
                        setExternalMainError('');
                      }}
                    >
                      Apply
                    </Button>
                  </>
                )}
              </div>
              {useExternalMain && externalMainError && (
                <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                  Image URL has an issue. See error dialog.
                </div>
              )}
              {hasGallery ? (
                <>
                  <div
                    onMouseDown={() => startLongPressMainSelect(image)}
                    onMouseUp={cancelLongPressMainSelect}
                    onMouseLeave={cancelLongPressMainSelect}
                    onTouchStart={() => startLongPressMainSelect(image)}
                    onTouchEnd={cancelLongPressMainSelect}
                  >
                    <Form.Control as='select' value={image || ''} onChange={(e) => setImage(e.target.value)} disabled={deleteBusy || useExternalMain}>
                      <option value=''>Select main image from gallery…</option>
                      {!hasCurrentImageOption && image && (
                        <option value={image}>Current: {image}</option>
                      )}
                      {imageSource.map((img, idx) => (
                        <option key={idx} value={img.src}>
                          {img.src}
                        </option>
                      ))}
                    </Form.Control>
                  </div>
                  {image && (
                    <div className='mt-2'>
                      <img
                        src={image}
                        alt='main-preview'
                        style={{ width: 120, height: 120, objectFit: 'cover', border: '1px solid #ddd', borderRadius: 4 }}
                      />
                    </div>
                  )}
                  <div className='text-muted' style={{ fontSize: 12, marginTop: 6 }}>Long-press the dropdown to delete the selected image.</div>
                </>
              ) : (
                <div className='text-muted'>Upload gallery files first, then choose one as main — or use an external URL above.</div>
              )}
              <div className='mt-2'>
                <Button variant='secondary' size='sm' onClick={() => openGallery({ type: 'main' })}>Browse Server Gallery</Button>
              </div>
            </Form.Group>

            <Form.Group controlId='video' className='pt-5'>
              <Form.Label>Video</Form.Label>
              <Form.Control
                type='text'
                value={video}
                onChange={(e) => setVideo(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='brand' className='pt-5'>
              <Form.Label>Brand</Form.Label>
              <Form.Control
                type='text'
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='countInStock' className='pt-5'>
              <Form.Label>Count In Stock</Form.Label>
              <Form.Control
                type='number'
                value={countInStock}
                onChange={(e) => setCountInStock(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='category' className='pt-5'>
              <Form.Label>Category</Form.Label>
              <CategoryPicker
                value={selectedCategoryId}
                prefillPath={prefillCategoryPath}
                onChange={(node) => {
                  if (!node) {
                    // Cleared selection: reset local state
                    setSelectedCategoryId(null);
                    setSelectedIsLeaf(false);
                    setPrefillCategoryPath([]);
                    return;
                  }
                  setSelectedCategoryId(node.id);
                  setSelectedIsLeaf(!!node.isLeaf);
                  setCategory(node.name || '');
                  setPrefillCategoryPath(Array.isArray(node.path) ? node.path : []);
                }}
              />
              {!!category && (
                <div className='text-muted' style={{ fontSize: 12, marginTop: 6 }}>
                  Current: {category}
                </div>
              )}
            </Form.Group>

            <Form.Group controlId='description' className='pt-5'>
              <Form.Label>Description</Form.Label>
              <Editor
                editorState={description}
                onEditorStateChange={(editorState) => setDescription(editorState)}
                toolbar={{
                  options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'history'],
                }}
              />
            </Form.Group>

            <Form.Group controlId='variantEditor' className='pt-5'>
              <Form.Label>Variants</Form.Label>
              {(!variantProps || variantProps.length === 0) && Array.isArray(colorsVariants) && colorsVariants.length > 0 && (
                <div className='mb-2'>
                  <Button variant='outline-primary' size='sm' onClick={convertLegacyVariants}>Convert legacy color variants → new variants</Button>
                </div>
              )}
              <VariantEditor
                marginPricing={pricing.pricingMethod === 'margin'}
                variantProps={variantProps}
                skus={skus}
                onChange={({ variantProps: vp, skus: nextSkus }) => { setVariantProps(vp || []); setSkus(nextSkus || []); }}
              />
            </Form.Group>

            <Form.Group controlId='colorVariants' className='pt-6'>
              <Form.Label>Color Variants</Form.Label>
              {colorsVariants.map((color, index) => {
                const variantHasOption = !!color.src && imageOptions.includes(color.src);
                return (
                  <div key={index} className='mb-3'>
                    <Form.Control
                      type='text'
                      placeholder='Enter color name'
                      value={color.name}
                      onChange={(e) => handleColorsVariantsChange(index, 'name', e.target.value)}
                      className='mb-2'
                    />
                    <Form.Control
                      type='number'
                      hidden={pricing.pricingMethod === 'margin'}
                      readOnly={pricing.pricingMethod === 'margin'}
                      placeholder='Variant price (optional)'
                      min='0'
                      step='0.01'
                      value={color.price === '' ? '' : color.price}
                      onChange={(e) => handleColorsVariantsChange(index, 'price', e.target.value)}
                      className='mb-2'
                    />
                    <Form.Check
                      type='checkbox'
                      id={`variant-unavailable-${index}`}
                      label='Unavailable for purchase'
                      checked={!!color.unavailable}
                      onChange={(e) => handleColorsVariantsChange(index, 'unavailable', e.target.checked)}
                      className='mb-2'
                    />
                    {hasGallery ? (
                      <>
                        <Form.Control
                          as='select'
                          value={color.src || ''}
                          onChange={(e) => handleColorsVariantsChange(index, 'src', e.target.value)}
                        >
                          <option value=''>Select variant image from gallery…</option>
                          {!variantHasOption && color.src && (
                            <option value={color.src}>Current: {color.src}</option>
                          )}
                          {imageSource.map((img, idx) => {
                            return (
                              <option key={idx} value={img.src}>
                                {img.src}
                              </option>
                            );
                          })}
                        </Form.Control>
                        <div className='mt-2'>
                          <Button variant='secondary' size='sm' onClick={() => openGallery({ type: 'variant', index })}>Browse Server Gallery</Button>
                        </div>
                        <div className='mt-2 d-flex align-items-center' style={{ gap: 8 }}>
                          <Form.Control
                            type='text'
                            placeholder='Or paste external image URL'
                            value={color.src || ''}
                            onChange={(e) => handleColorsVariantsChange(index, 'src', e.target.value)}
                          />
                          <Button
                            variant='outline-secondary'
                            size='sm'
                            onClick={() => {
                              const val = (color.src || '').trim();
                              if (val) {
                                handleColorsVariantsChange(index, 'src', val);
                                ensureInGallery(val);
                              }
                            }}
                          >
                            Apply URL
                          </Button>
                        </div>
                        {color.src && (
                          <div className='mt-2'>
                            <img
                              src={color.src}
                              alt={`variant-${index}`}
                              style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #ddd', borderRadius: 4 }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className='text-muted'>Upload gallery files first, then select images for variants.</div>
                    )}
                    <Button
                      variant='danger'
                      onClick={() => removeColorsVariants(index)}
                      className='mt-2'
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}
              <Button variant='primary' onClick={addColorsVariants} className='mt-2'>
                Add Color
              </Button>
            </Form.Group>

            <div className='mt-4 d-flex' style={{ gap: 8 }}>
              <Button type='button' onClick={submitHandler} variant='primary' disabled={loadingUpdate || submitting}>
                {loadingUpdate || submitting ? 'Updating…' : 'Update Product'}
              </Button>
              <Button type='button' variant='secondary' onClick={() => history.push('/admin/productlist')}>
                Back to Product List
              </Button>
              <Link to={viewProductPath} className='btn btn-outline-primary'>
                View Product
              </Link>
            </div>
            </Form>
          </>
        )}
      </FormContainer>

      {/* Unsaved Changes Modal */}
      <Modal show={showUnsavedModal} onHide={handleUnsavedCancel} backdrop='static' keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Do you want to save before leaving?
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={handleUnsavedSaveAndExit} disabled={loadingUpdate || submitting}>
            {loadingUpdate || submitting ? 'Saving…' : 'Save and Exit'}
          </Button>
          <Button variant='secondary' onClick={handleUnsavedExitWithoutSave}>
            Don’t Save and Exit
          </Button>
          <Button variant='danger' onClick={handleUnsavedCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Saved Toast */}
      <div aria-live='polite' aria-atomic='true' style={{ position: 'fixed', top: 12, right: 12, zIndex: 1060 }}>
        <Toast onClose={() => setShowSavedToast(false)} show={showSavedToast} delay={2500} autohide>
          <Toast.Header>
            <strong className='mr-auto'>Saved</strong>
          </Toast.Header>
          <Toast.Body>Product saved successfully.</Toast.Body>
        </Toast>
      </div>

      <Modal show={showUploadImageModal} onHide={handleConfirmClose}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {successImageUpload ? (
            <Message variant='success'>Images Uploaded Successfully!</Message>
          ) : (
            <div className='text-muted'>No status to display.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='danger' onClick={handleConfirmClose}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Gallery Modal */}
      <Modal show={showGallery} onHide={closeGallery} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {galleryTarget?.type === 'variant' && galleryTarget.index !== null
              ? `Choose Image for Color Variant #${galleryTarget.index + 1} from /uploads/${imageFolderName} and product URLs`
              : `Choose Main Image from /uploads/${imageFolderName} and product URLs`}
          </Modal.Title>
          <div className='ml-auto d-flex align-items-center' style={{ gap: 8 }}>
            <Button variant={deleteMode ? 'outline-secondary' : 'outline-danger'} size='sm' onClick={() => { setDeleteMode((v) => !v); clearSelection(); setDeleteError(''); }} disabled={uploadingFiles || loadingImageUpload || deleteBusy}>
              {deleteMode ? 'Exit Delete' : 'Delete Mode'}
            </Button>
            {deleteMode && (
              <>
                <Button variant='outline-primary' size='sm' onClick={selectAllInGallery} disabled={uploadingFiles || loadingImageUpload || deleteBusy || galleryItems.length === 0}>Select All</Button>
                <Button variant='danger' size='sm' onClick={() => openDeleteConfirm({ type: 'multi', srcs: Array.from(selectedToDelete) })} disabled={uploadingFiles || loadingImageUpload || deleteBusy || selectedToDelete.size === 0}>Delete selected ({selectedToDelete.size})</Button>
              </>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          {galleryLoading && <Loader />}
          {galleryError && !galleryLoading && (
            <div className='text-muted'>Failed to load gallery. See error dialog.</div>
          )}
          {!galleryLoading && !galleryError && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {galleryItems.length === 0 ? (
                <div className='text-muted'>No images found in this folder or product gallery.</div>
              ) : (
                galleryItems.map((item, i) => {
                  const src = item?.src;
                  const isSelected = deleteMode && src && selectedToDelete.has(src);
                  return (
                    <div key={i} style={{ textAlign: 'center', position: 'relative' }}>
                      {src && isValidHttpUrl(src) && (
                        <span style={{ position: 'absolute', top: 6, right: 6, background: '#17a2b8', color: '#fff', fontSize: 10, padding: '2px 4px', borderRadius: 3 }}>URL</span>
                      )}
                      <img
                        src={src}
                        alt={`img-${i}`}
                        style={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer', borderRadius: 4, border: isSelected ? '2px solid #dc3545' : '1px solid #ddd', boxShadow: isSelected ? '0 0 0 2px rgba(220,53,69,.25)' : 'none' }}
                        onClick={() => { if (deleteMode) { if (src) toggleSelectToDelete(src); } else { selectGalleryImage(src); } }}
                      />
                      {deleteMode && (
                        <input type='checkbox' checked={!!src && selectedToDelete.has(src)} onChange={() => src && toggleSelectToDelete(src)} style={{ position: 'absolute', top: 6, left: 6 }} disabled={deleteBusy} />
                      )}
                      <div style={{ maxWidth: 100, wordBreak: 'break-word', fontSize: 12 }}>
                        {src?.split('/').pop()}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeGallery}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete images?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You’re about to delete {deleteContext?.srcs?.length || 0} image(s). This can’t be undone.</p>
          {deleteError && <div className='text-muted'>Delete failed. See error dialog.</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteConfirm(false)} disabled={deleteBusy}>Cancel</Button>
          <Button variant='danger' onClick={onConfirmDelete} disabled={deleteBusy}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Unified Error Modal */}
      <Modal show={errorModal.show} onHide={closeErrorModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{errorModal.title || 'Error'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message variant='danger'>{errorModal.message}</Message>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeErrorModal}>Dismiss</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductEditScreen;


